import React, { useEffect } from "react";
import AssistantSite from "./components/AssistantSite";
import MesureVisites from "./components/MesureVisites";
import BandeauVersion from "./components/BandeauVersion";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate } from "react-router-dom";
import EcoleApp from "./ecole/EcoleApp";
import { RetourApp } from "./components/RetourApp";
import { MiseAJour } from "./components/MiseAJour";

import Navbar from "./components/Navbar";
import AnnouncementBar from "./components/AnnouncementBar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Cours from "./pages/Cours";
import CourseDetail from "./pages/CourseDetail";
import Planning from "./pages/Planning";
import Tarifs from "./pages/Tarifs";
import Prestations from "./pages/Prestations";
import Stages from "./pages/Stages";
import StageDetail from "./pages/StageDetail";
import Spectacles from "./pages/Spectacles";
import Actualites from "./pages/Actualites";
import Inscription from "./pages/Inscription";
import Contact from "./pages/Contact";
import Competition from "./pages/Competition";
import Sponsoring from "./pages/Sponsoring";
import Assurances from "./pages/Assurances";
import Ecole from "./pages/Ecole";
import Professeurs from "./pages/Professeurs";
import Faq from "./pages/Faq";
import MentionsLegales from "./pages/MentionsLegales";
import Reglement from "./pages/Reglement";
import AuditionForm from "./pages/AuditionForm";
import CoursPrivesForm from "./pages/CoursPrivesForm";
import WorkshopsHeels from "./pages/WorkshopsHeels";
import WorkshopForm from "./pages/WorkshopForm";
import Carte10Form from "./pages/Carte10Form";
import SpectacleReservation from "./pages/SpectacleReservation";
import BilletsSpectacles from "./pages/BilletsSpectacles";
import BilletsMerci, { Billet } from "./pages/BilletsMerci";
import Entree from "./pages/Entree";
import { MonCoursPrive } from "./pages/ReservationCoursPrive";
import MonBenevolat from "./pages/MonBenevolat";
import AvisEssai from "./pages/AvisEssai";
import DatesImportantes from "./pages/DatesImportantes";
// Console d'administration (chantier) : page isolée, aucun lien depuis le site public.
import Console from "./console/Console";
import NotFound from "./pages/NotFound";
import MonAvs from "./pages/MonAvs";
import Espace from "./espace/Espace";
import EspaceProf from "./prof/EspaceProf";
import Desinscription from "./pages/Desinscription";
import MonEssai from "./pages/MonEssai";
import MonSpectacle from "./pages/MonSpectacle";
import PortesOuvertes from "./pages/PortesOuvertes";
import { useReglages } from "./lib/useReglages";
import GardeErreur from "./components/GardeErreur";

// Anciennes URL du site WordPress → équivalent sur le nouveau site.
// Les vraies redirections 301 doivent être posées au niveau DNS/CDN (Cloudflare
// Bulk Redirects) : la plateforme d'hébergement ne permet pas de renvoyer un code
// HTTP 301. Ces routes sont un filet de sécurité côté navigateur pour que les
// visiteurs qui suivent un vieux lien arrivent au bon endroit malgré tout.
const LEGACY_REDIRECTS = [
  ["/accueil", "/"],
  ["/nos-cours", "/cours"],
  ["/prestations-supplementaires", "/evenements"],
  ["/formulaire", "/inscription"],
  ["/merci-liste-attente", "/inscription"],
  ["/calendar/evenements", "/actualites"],
  ["/cours/mini-heels", "/cours/mini-girly"],
  ["/cours/condition-physique", "/cours"],
  ["/cours/fit-dance", "/cours"],
  ["/stages/paques-2027", "/stages/automne-2027"],
];

function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    // Retour arrière : on laisse le navigateur restaurer la position du visiteur.
    if (navType === "POP") return undefined;
    // Nouvelle page : on remonte en haut. Un seul appel ne suffit pas sur mobile — le
    // navigateur réapplique parfois la position enregistrée au moment du pushState
    // juste APRÈS notre remontée (c'est ce qui faisait atterrir en bas de page, de
    // façon aléatoire). On réapplique donc au frame suivant puis brièvement après.
    const toTop = () => window.scrollTo(0, 0);
    toTop();
    const raf = requestAnimationFrame(toTop);
    const timers = [setTimeout(toTop, 60), setTimeout(toTop, 250)];
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [pathname, navType]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main style={{ paddingTop: "var(--annonce-h, 0px)" }}>{children}</main>
      <Footer />
      <AssistantSite />
    </>
  );
}

function App() {
  // Les réglages de la console (tarifs, montants, stages, date d'audition, nombre d'élèves)
  // sont chargés une fois pour toute la visite : chaque page les voit appliqués, même si on
  // y arrive directement.
  useReglages();
  return (
    <div className="App">
      <GardeErreur>
        <BrowserRouter>
          <ScrollToTop />
          {/* Les visites du site (pages vues, temps passé), pour le tableau de bord de la
              console : sans cookie, ni les applications ni l'école. */}
          <MesureVisites />
          {/* « VERSION DE TEST » en haut de chaque page (site, console, apps) sur la copie de
              test ; bandeau rouge si le site est branché sur le mauvais serveur. */}
          <BandeauVersion />
          <RetourApp />
          <MiseAJour />
          <Routes>
            {/* L'ancienne page d'administration (Emergent) est retirée le 26.10.2026 :
                tout se fait dans la console. Un ancien favori y mène. */}
            <Route path="/admin" element={<Navigate to="/console" replace />} />
            <Route path="/console" element={<Console />} />
            {/* L'espace famille est une application, pas une page du site : il sort du
                gabarit public (bandeau d'annonce, menu marketing, pied de page) et porte sa
                propre barre d'onglets. */}
            <Route path="/espace" element={<Espace />} />
            <Route path="/prof" element={<EspaceProf />} />
            {/* Le mode école de la direction : /direction. Sous /ecole il cachait la page
                publique « L'école » du site (menu, Google, plan du site). */}
            <Route path="/direction" element={<EcoleApp />} />
            {/* L'entrée d'un spectacle (bénévoles, lien à clé) : plein écran, sans le site autour. */}
            <Route path="/entree" element={<Entree />} />
            <Route path="*" element={
              <PublicLayout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/cours" element={<Cours />} />
                  <Route path="/cours/:slug" element={<CourseDetail />} />
                  <Route path="/planning" element={<Planning />} />
                  <Route path="/competition" element={<Competition />} />
                  <Route path="/sponsoring" element={<Sponsoring />} />
                  <Route path="/assurances" element={<Assurances />} />
                  <Route path="/inscription/audition" element={<AuditionForm />} />
                  <Route path="/inscription/cours-prive" element={<CoursPrivesForm />} />
                  <Route path="/inscription/workshop" element={<WorkshopForm />} />
                  <Route path="/inscription/carte-10" element={<Carte10Form />} />
                  <Route path="/inscription/spectacle" element={<SpectacleReservation />} />
                  <Route path="/dates-importantes" element={<DatesImportantes />} />
                  <Route path="/tarifs" element={<Tarifs />} />
                  <Route path="/prestations" element={<Prestations />} />
                  <Route path="/evenements" element={<Prestations />} />
                  {/* La page des workshops heels passe AVANT le slug générique des prestations. */}
                  <Route path="/evenements/workshops-heels" element={<WorkshopsHeels />} />
                  <Route path="/evenements/:slug" element={<Prestations />} />
                  <Route path="/stages" element={<Stages />} />
                  <Route path="/stages/:stageId" element={<StageDetail />} />
                  <Route path="/spectacles" element={<Spectacles />} />
                  <Route path="/spectacles/billets" element={<BilletsSpectacles />} />
                  <Route path="/spectacles/billets/merci" element={<BilletsMerci />} />
                  <Route path="/billet/:jeton" element={<Billet />} />
                  <Route path="/mon-cours-prive" element={<MonCoursPrive />} />
                  {/* Le lien personnel d'un bénévole des spectacles : changer ou annuler son créneau. */}
                  <Route path="/mon-benevolat" element={<MonBenevolat />} />
                  <Route path="/avis-essai" element={<AvisEssai />} />
                  <Route path="/actualites" element={<Actualites />} />
                  <Route path="/equipe" element={<Professeurs />} />
                  <Route path="/ecole" element={<Ecole />} />
                  <Route path="/professeurs" element={<Professeurs />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/mentions-legales" element={<MentionsLegales />} />
                  <Route path="/reglement" element={<Reglement />} />
                  <Route path="/reglement-competition" element={<Reglement />} />
                  <Route path="/confidentialite" element={<MentionsLegales />} />
                  <Route path="/inscription" element={<Inscription />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/mon-avs" element={<MonAvs />} />
                  <Route path="/desinscription" element={<Desinscription />} />
                  <Route path="/mon-essai" element={<MonEssai />} />
                  {/* Le lien personnel d'une réservation du spectacle de Noël : changer le nombre ou annuler. */}
                  <Route path="/mon-spectacle" element={<MonSpectacle />} />
                  {/* Page d'atterrissage : volontairement absente du menu. Elle existe pour
                      donner aux robots et aux assistants IA une URL unique portant les dates
                      exactes des Portes Ouvertes, en vrai HTML (prérendue au build). */}
                  <Route path="/portes-ouvertes" element={<PortesOuvertes />} />
                  {LEGACY_REDIRECTS.map(([from, to]) => (
                    <Route key={from} path={from} element={<Navigate to={to} replace />} />
                  ))}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </PublicLayout>
            } />
          </Routes>
        </BrowserRouter>
      </GardeErreur>
    </div>
  );
}

export default App;
